import { z } from 'zod';
import { FlowInputFieldSchema } from './input.js';

export const FlowNodeTypeSchema = z.enum([
  'trigger',
  'filter',
  'agent',
  'agent_task',
  'approval',
  'router',
  'llm',
  'action',
]);
export type FlowNodeType = z.infer<typeof FlowNodeTypeSchema>;

export const FlowJoinTypeSchema = z.enum(['and', 'or']);
export type FlowJoinType = z.infer<typeof FlowJoinTypeSchema>;

export const FlowPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type FlowPosition = z.infer<typeof FlowPositionSchema>;

export const FlowApprovalTypeSchema = z.enum([
  'understanding_review',
  'plan_review',
  'pr_review',
  'generic',
]);
export type FlowApprovalType = z.infer<typeof FlowApprovalTypeSchema>;

export const FlowApprovalDecisionOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});
export type FlowApprovalDecisionOption = z.infer<typeof FlowApprovalDecisionOptionSchema>;

/**
 * A target an approval (HUM) node "reports to": a single user, a team, or
 * the whole organization. Mirrors the `ResourceOwnerType` vocabulary so an
 * approval can be routed to one or more users/teams, or to everyone in the
 * org. When an approval node leaves `reportTo` empty, the engine derives the
 * effective targets from the owning flow.
 */
export const FlowApprovalAssigneeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), userId: z.string().min(1) }),
  z.object({ kind: z.literal('team'), teamId: z.string().min(1) }),
  z.object({ kind: z.literal('organization') }),
]);
export type FlowApprovalAssignee = z.infer<typeof FlowApprovalAssigneeSchema>;

export const FlowRouterStrategySchema = z.enum(['expression', 'llm']);
export type FlowRouterStrategy = z.infer<typeof FlowRouterStrategySchema>;

export const FlowLLMResponseFormatSchema = z.enum(['text', 'json']);
export type FlowLLMResponseFormat = z.infer<typeof FlowLLMResponseFormatSchema>;

// --- Filter config ---
// Declared before trigger config so event triggers can embed a filter
// (FlowEventTriggerConfigSchema.filter) using the same shape that standalone
// `filter` nodes use.

export const FlowFilterOpSchema = z.enum([
  'equals',
  'not_equals',
  'contains',
  'in',
  'intersects',
  'exists',
  'not_exists',
  'truthy',
]);
export type FlowFilterOp = z.infer<typeof FlowFilterOpSchema>;

/**
 * One declarative condition evaluated against the run's template context.
 * `path` is a dotted context path (e.g. `trigger.channelId`).
 *
 * `ifMissing` decides the outcome when the path resolves to undefined/null:
 * `'fail'` (default) rejects, `'pass'` treats the condition as vacuously
 * true. The distinction preserves legacy trigger-filter semantics: slack
 * filters failed on missing fields while github/sentry/linear/pylon filters
 * only applied when the payload carried the field. The presence ops
 * (`exists`, `not_exists`, `truthy`) answer for themselves on a missing path,
 * so `ifMissing` does not apply to them.
 */
export const FlowFilterConditionSchema = z.object({
  path: z.string().min(1),
  op: FlowFilterOpSchema,
  value: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.array(z.number())])
    .optional(),
  caseInsensitive: z.boolean().optional(),
  ifMissing: z.enum(['pass', 'fail']).optional().default('fail'),
});
export type FlowFilterCondition = z.infer<typeof FlowFilterConditionSchema>;

/**
 * Filter shape shared by standalone `filter` nodes and the optional
 * trigger-embedded filter. A filter is EITHER condition-based or
 * expression-based, never both — the `mode` discriminator makes the active
 * type unambiguous in the editor and the engine. The branch (or run dispatch)
 * proceeds when the active side holds; otherwise it is skipped.
 */
export const FlowConditionsFilterConfigSchema = z
  .object({
    mode: z.literal('conditions'),
    conditions: z.array(FlowFilterConditionSchema).optional(),
    match: z.enum(['all', 'any']).optional().default('all'),
  })
  .strict();
export type FlowConditionsFilterConfig = z.infer<typeof FlowConditionsFilterConfigSchema>;

export const FlowExpressionFilterConfigSchema = z
  .object({
    mode: z.literal('expression'),
    /** Template expression (same language as router `expression`) that must resolve truthy. */
    expression: z.string().min(1),
  })
  .strict();
export type FlowExpressionFilterConfig = z.infer<typeof FlowExpressionFilterConfigSchema>;

const FlowFilterConfigUnionSchema = z.discriminatedUnion('mode', [
  FlowConditionsFilterConfigSchema,
  FlowExpressionFilterConfigSchema,
]);

export const FlowFilterNodeConfigSchema = z.preprocess(
  (raw) => normalizeLegacyFilterConfig(raw),
  FlowFilterConfigUnionSchema,
);
export type FlowFilterNodeConfig = z.infer<typeof FlowFilterNodeConfigSchema>;

/**
 * Normalize a legacy `mode`-less filter config (conditions/match/expression all
 * optional and implicitly ANDed) into the discriminated shape, so rows written
 * before the `mode` discriminator existed still parse. Conditions win when both
 * sides were present (the rare legacy case) and the stray expression is dropped.
 * Pure and idempotent: a config that already carries `mode` is returned as-is.
 */
function normalizeLegacyFilterConfig(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || 'mode' in raw) return raw;
  const r = raw as Record<string, unknown>;
  const hasConditions = Array.isArray(r.conditions) && r.conditions.length > 0;
  if (!hasConditions && typeof r.expression === 'string' && r.expression.length > 0) {
    return { mode: 'expression', expression: r.expression };
  }
  const rest = { ...r };
  delete rest.expression;
  return { mode: 'conditions', ...rest };
}

/**
 * Migration-facing wrapper around the legacy filter normalizer. Returns a
 * typed {@link FlowFilterNodeConfig} (defaulting `match` to `all` for
 * conditions filters) without throwing on already-validated input.
 */
export function normalizeFilterConfigToMode(raw: unknown): FlowFilterNodeConfig {
  const normalized = normalizeLegacyFilterConfig(raw) as Record<string, unknown>;
  if (normalized.mode === 'expression') {
    return { mode: 'expression', expression: String(normalized.expression ?? '') };
  }
  return {
    mode: 'conditions',
    ...(Array.isArray(normalized.conditions)
      ? { conditions: normalized.conditions as FlowConditionsFilterConfig['conditions'] }
      : {}),
    match: normalized.match === 'any' ? 'any' : 'all',
  };
}

// --- Trigger node config ---

// Trigger nodes are the entry points of a graph. A flow may carry several;
// each run is anchored at exactly one fired trigger node (the others are
// skipped). Event triggers match on event identity (source/event/action) and
// may carry an OPTIONAL embedded `filter` evaluated at dispatch to gate run
// creation. Standalone `filter` nodes downstream still work unchanged; the
// embedded filter is additive (see FlowEventTriggerConfigSchema.filter).

/**
 * Fires when a matching `ReflexEvent` arrives on the host event bus.
 * `source`/`event` identify the event (see the event catalog); `action`
 * optionally narrows to a single `ReflexEvent.action` value.
 *
 * `filter` is optional and event-only: when set it is evaluated at DISPATCH
 * (before a run is created) against the event payload. If it rejects, no run
 * row is created at all. This is additive to downstream standalone `filter`
 * nodes, which continue to gate mid-run as before.
 */
export const FlowEventTriggerConfigSchema = z
  .object({
    kind: z.literal('event'),
    source: z.string().min(1),
    event: z.string().min(1),
    action: z.string().optional(),
    filter: FlowFilterNodeConfigSchema.optional(),
  })
  .strict();
export type FlowEventTriggerConfig = z.infer<typeof FlowEventTriggerConfigSchema>;

/** Fires on a cron schedule. */
export const FlowCronTriggerConfigSchema = z
  .object({
    kind: z.literal('cron'),
    schedule: z.string().min(1).max(256),
    timezone: z.string().max(128).optional(),
  })
  .strict();
export type FlowCronTriggerConfig = z.infer<typeof FlowCronTriggerConfigSchema>;

/**
 * Fires only when run manually. `fields` describe the payload form shown in
 * the run dialog (the successor of the flow-level `inputSchema`). Every
 * trigger node can be fired manually with event-shaped data; a `manual`
 * trigger is simply one with no subscription at all.
 */
export const FlowManualTriggerConfigSchema = z
  .object({
    kind: z.literal('manual'),
    fields: z.array(FlowInputFieldSchema).optional(),
    /**
     * Optional domain model name (e.g. `SentryIssue`) the manual payload
     * conforms to. Plugin UIs use it to discover flows runnable against
     * their entities; carried over from the legacy manual trigger's `model`.
     */
    model: z.string().optional(),
  })
  .strict();
export type FlowManualTriggerConfig = z.infer<typeof FlowManualTriggerConfigSchema>;

/**
 * Fires when a backend service starts a run programmatically (e.g. the
 * project profiler), rather than a person clicking Run or an inbound
 * webhook/cron. System-owned flows use this so run surfaces can attribute
 * the run to the originating service and its subject resource instead of
 * showing a bare "Manual" badge.
 */
export const FlowServiceTriggerConfigSchema = z
  .object({
    kind: z.literal('service'),
    /**
     * Stable id of the service that owns runs from this trigger
     * (e.g. `'project-profiler'`). Used to key the run-surface presentation.
     */
    service: z.string().min(1),
    /** Human label shown on run/editor surfaces (e.g. `'Project Profiler'`). */
    label: z.string().optional(),
    /**
     * Domain model this flow operates on (e.g. `'CodeProject'`), kept for
     * model-based discovery parity with manual triggers.
     */
    model: z.string().optional(),
  })
  .strict();
export type FlowServiceTriggerConfig = z.infer<typeof FlowServiceTriggerConfigSchema>;

export const FlowTriggerNodeConfigSchema = z.discriminatedUnion('kind', [
  FlowEventTriggerConfigSchema,
  FlowCronTriggerConfigSchema,
  FlowManualTriggerConfigSchema,
  FlowServiceTriggerConfigSchema,
]);
export type FlowTriggerNodeConfig = z.infer<typeof FlowTriggerNodeConfigSchema>;
export type FlowTriggerKind = FlowTriggerNodeConfig['kind'];

// --- Agent launch field schemas ---
//
// These mirror the canonical `AgentEnvVar`, `PluginAttachmentValue`, and
// `ResourceSize` schemas in `@reflex/shared`. They retain the historical stored-graph
// validation contract. Consolidating them must preserve compatibility (the
// canonical environment-variable schema has additional refinements).
// The plugin-flows agent adapter maps these onto the canonical shared types
// when launching an agent.

/** Mirrors `@reflex/shared` `AgentEnvVarSchema` (without the cross-field refine). */
export const FlowAgentEnvVarSchema = z.object({
  key: z.string().min(1),
  value: z.string().optional(),
  secretRef: z.string().optional(),
  isSecret: z.boolean().optional(),
});
export type FlowAgentEnvVar = z.infer<typeof FlowAgentEnvVarSchema>;

/** Mirrors `@reflex/shared` `PluginAttachmentValueSchema`. */
export const FlowPluginAttachmentSchema = z.object({
  attachmentId: z.string(),
  pluginName: z.string(),
  config: z.unknown().optional(),
});
export type FlowPluginAttachment = z.infer<typeof FlowPluginAttachmentSchema>;

/** Mirrors `@reflex/shared` `ResourceSizeSchema`. */
export const FlowResourceSizeSchema = z.enum(['SMALL', 'MEDIUM', 'LARGE', 'X_LARGE', 'XX_LARGE']);
export type FlowResourceSize = z.infer<typeof FlowResourceSizeSchema>;

/**
 * Upper bound for prompt-bearing template fields (agent/agent_task/LLM prompt
 * templates, approval prompts, router llmPrompt). Generous for real prompts
 * (the longest shipped template is ~2KB) while bounding the size of any single
 * string the engine resolves at run time. Substitution and conditional
 * rendering are linear, so this is defense-in-depth at the persistence
 * boundary, not the primary safety guard.
 */
const MAX_PROMPT_CHARS = 100_000;

// --- Agent node config ---

export const FlowAgentNodeConfigSchema = z.object({
  promptTemplate: z.string().min(1).max(MAX_PROMPT_CHARS),
  model: z.string().optional(),
  /**
   * Specific model endpoint (`me_*`) for multi-model agents. Forwarded to the
   * runner's `modelEndpointId`. When omitted the runner resolves the default
   * endpoint for the chosen model.
   */
  modelEndpointId: z.string().optional(),
  /**
   * Agent type to launch (e.g. `claude-code`, `codex`). When omitted the
   * adapter falls back to the flow/runner default.
   */
  agentType: z.string().optional(),
  /**
   * Pinned model-provider secret (`mps_*`) id for this node. Owner-scope is
   * validated against the flow's owner on save and re-checked at runtime.
   */
  providerSecretId: z.string().optional(),
  /** Persona to run this agent with. Provides system prompt, env vars, attachments, etc. */
  personaId: z.string().optional(),
  repoSource: z.enum(['trigger', 'explicit']).optional(),
  /** GitHub repository in 'owner/repo' format (e.g., 'runloop/reflex') */
  repoSlug: z.string().optional(),
  repoBranch: z.string().optional(),
  blueprintName: z.string().optional(),
  /**
   * Devbox disk snapshot id (`snp_*`) to launch from instead of a blueprint.
   * Mutually exclusive with `blueprintName` at runtime.
   */
  snapshotId: z.string().optional(),
  /** Devbox resource size. Falls back to the blueprint's configured size when omitted. */
  resourceSize: FlowResourceSizeSchema.optional(),
  /** Start a Docker daemon (Docker-in-Docker) inside the devbox at launch. */
  dockerd: z.boolean().optional(),
  /**
   * Minutes the devbox may sit idle before auto-suspending. Falls back to the
   * provider default when omitted.
   */
  idleTimeMinutes: z.number().int().positive().optional(),
  /** Environment variables injected into the agent's devbox. */
  envVars: z.array(FlowAgentEnvVarSchema).optional(),
  /** Plugin attachments (e.g. the `git-repo` attachment) mounted at launch. */
  attachments: z.array(FlowPluginAttachmentSchema).optional(),
  /**
   * Agent labels (`alb_*`) put on every run this node launches. A flow is the
   * launch surface where filing after the fact is least likely to happen —
   * nobody is watching when the run starts — so the node says up front what
   * kind of work its runs are. Existence is not resolved in graph schemas; the adapter hands these ids
   * to the runner, which skips any that no longer exist.
   */
  labelIds: z.array(z.string()).optional(),
  /** Agent group (`agp_*`) every run this node launches is filed into. */
  groupId: z.string().optional(),
  stateOutputMapping: z.record(z.string(), z.string()).optional(),
  useMemory: z.boolean().optional(),
  saveToMemory: z.boolean().optional(),
});
export type FlowAgentNodeConfig = z.infer<typeof FlowAgentNodeConfigSchema>;

// --- Agent task node config ---

/**
 * Max `setTimeout` delay. Node coerces anything larger (past the 32-bit max,
 * ~24.8 days) to 1ms, so timeout values that reach a timer are clamped to this.
 */
export const MAX_FLOW_TIMER_MS = 2_147_483_647;

export const FlowAgentTaskNodeConfigSchema = FlowAgentNodeConfigSchema.extend({
  // New agent_task nodes default to structured JSON so downstream nodes can
  // read individual fields ({{input.field}}); already-persisted nodes keep
  // whatever value they were saved with.
  responseFormat: FlowLLMResponseFormatSchema.optional().default('json'),
  outputKey: z.string().optional().default('result'),
  jsonSchema: z.record(z.string(), z.unknown()).optional(),
  /**
   * Hard cap on this task's total runtime, in ms. Overrides the engine default
   * (`FlowLaunchConfig.nodeTimeoutMs`); omitted falls back to it.
   */
  timeoutMs: z.number().int().positive().max(MAX_FLOW_TIMER_MS).optional(),
});
export type FlowAgentTaskNodeConfig = z.infer<typeof FlowAgentTaskNodeConfigSchema>;

// --- Approval node config ---

export const FlowApprovalNodeConfigSchema = z.object({
  approvalType: FlowApprovalTypeSchema,
  prompt: z.string().min(1).max(MAX_PROMPT_CHARS),
  allowFeedback: z.boolean().optional().default(true),
  decisionOptions: z.array(FlowApprovalDecisionOptionSchema).min(1).optional(),
  /**
   * Who this approval is reported to and gated on. Each entry is a user,
   * team, or the organization. When omitted/empty the engine falls back to
   * the run's owner (the whole org when that owner is a service account,
   * the owning flow's owner when the run has none). Only these targets see
   * the approval in their inbox and may approve/reject it.
   */
  reportTo: z.array(FlowApprovalAssigneeSchema).optional(),
});
export type FlowApprovalNodeConfig = z.infer<typeof FlowApprovalNodeConfigSchema>;

// --- Router node config ---

export const FlowRouterNodeConfigSchema = z.object({
  labels: z.array(z.string()).min(2),
  strategy: FlowRouterStrategySchema,
  expression: z.string().optional(),
  llmPrompt: z.string().max(MAX_PROMPT_CHARS).optional(),
  /** Model used for `llm`-strategy classification. */
  model: z.string().optional(),
  /**
   * Pinned model-provider secret (`mps_*`) id used for `llm`-strategy
   * classification. Owner-scope validated on save and at runtime.
   */
  providerSecretId: z.string().optional(),
});
export type FlowRouterNodeConfig = z.infer<typeof FlowRouterNodeConfigSchema>;

// --- LLM node config ---

export const FlowLLMNodeConfigSchema = z.object({
  promptTemplate: z.string().min(1).max(MAX_PROMPT_CHARS),
  model: z.string().optional(),
  /**
   * Pinned model-provider secret (`mps_*`) id for this node. Owner-scope is
   * validated against the flow's owner on save and re-checked at runtime.
   */
  providerSecretId: z.string().optional(),
  responseFormat: FlowLLMResponseFormatSchema.optional().default('text'),
  outputKey: z.string().optional().default('llmResult'),
  stateOutputMapping: z.record(z.string(), z.string()).optional(),
  useMemory: z.boolean().optional(),
  saveToMemory: z.boolean().optional(),
});
export type FlowLLMNodeConfig = z.infer<typeof FlowLLMNodeConfigSchema>;

// --- Action node config ---

// Action nodes model first-class side effects against external systems.
// The engine stays generic; plugins provide concrete handlers keyed by
// provider + operation (for example, sentry.update_issue).
// `provider`/`operation` are empty on a node nobody has configured yet: the
// editor drops an action node before the user has picked what it runs, and a
// schema that forbade that would force the editor to invent a default — a
// real, runnable action the user never chose. Empty is not publishable:
// `ACTION_NOT_CONFIGURED` is a strict (save/publish) validation error, the
// same way an explicit-repo agent node ships with an empty `repoSlug`.
export const FlowActionNodeConfigSchema = z.object({
  provider: z.string(),
  operation: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
  stateOutputMapping: z.record(z.string(), z.string()).optional(),
});
export type FlowActionNodeConfig = z.infer<typeof FlowActionNodeConfigSchema>;

// --- Node I/O field schema ---

export const FlowIOFieldSchema = z.object({
  key: z.string().min(1),
  type: z.enum(['string', 'number', 'boolean', 'json']).optional(),
  description: z.string().optional(),
  /**
   * Semantic reference type (`'<provider>:<kind>'`, e.g. `'slack:user'`)
   * when the field carries an external entity id. Run surfaces use it to
   * resolve the id into a display badge; see
   * `@reflex/plugin-api/external-refs`.
   */
  fieldType: z.string().optional(),
});
export type FlowIOField = z.infer<typeof FlowIOFieldSchema>;

// --- Base fields shared by all nodes ---

const flowNodeBase = {
  id: z.string().min(1),
  name: z.string().min(1),
  joinType: FlowJoinTypeSchema.optional().default('and'),
  maxIterations: z.number().int().positive().optional().default(5),
  position: FlowPositionSchema,
};

// --- Discriminated union ---

export const FlowTriggerNodeSchema = z.object({
  type: z.literal('trigger'),
  ...flowNodeBase,
  config: FlowTriggerNodeConfigSchema,
});
export type FlowTriggerNode = z.infer<typeof FlowTriggerNodeSchema>;

export const FlowFilterNodeSchema = z.object({
  type: z.literal('filter'),
  ...flowNodeBase,
  config: FlowFilterNodeConfigSchema,
});
export type FlowFilterNode = z.infer<typeof FlowFilterNodeSchema>;

export const FlowAgentNodeSchema = z.object({
  type: z.literal('agent'),
  ...flowNodeBase,
  config: FlowAgentNodeConfigSchema,
});
export type FlowAgentNode = z.infer<typeof FlowAgentNodeSchema>;

export const FlowAgentTaskNodeSchema = z.object({
  type: z.literal('agent_task'),
  ...flowNodeBase,
  config: FlowAgentTaskNodeConfigSchema,
});
export type FlowAgentTaskNode = z.infer<typeof FlowAgentTaskNodeSchema>;

export const FlowApprovalNodeSchema = z.object({
  type: z.literal('approval'),
  ...flowNodeBase,
  config: FlowApprovalNodeConfigSchema,
});
export type FlowApprovalNode = z.infer<typeof FlowApprovalNodeSchema>;

export const FlowRouterNodeSchema = z.object({
  type: z.literal('router'),
  ...flowNodeBase,
  config: FlowRouterNodeConfigSchema,
});
export type FlowRouterNode = z.infer<typeof FlowRouterNodeSchema>;

export const FlowLLMNodeSchema = z.object({
  type: z.literal('llm'),
  ...flowNodeBase,
  config: FlowLLMNodeConfigSchema,
});
export type FlowLLMNode = z.infer<typeof FlowLLMNodeSchema>;

export const FlowActionNodeSchema = z.object({
  type: z.literal('action'),
  ...flowNodeBase,
  config: FlowActionNodeConfigSchema,
});
export type FlowActionNode = z.infer<typeof FlowActionNodeSchema>;

export const FlowNodeSchema = z.discriminatedUnion('type', [
  FlowTriggerNodeSchema,
  FlowFilterNodeSchema,
  FlowAgentNodeSchema,
  FlowAgentTaskNodeSchema,
  FlowApprovalNodeSchema,
  FlowRouterNodeSchema,
  FlowLLMNodeSchema,
  FlowActionNodeSchema,
]);
export type FlowNode = z.infer<typeof FlowNodeSchema>;

// --- Shared JSON Schema definitions ---

// Registry ids for the sub-schemas that appear more than once inside
// FlowNodeSchema. `z.toJSONSchema` hoists a registered schema into
// `definitions` and emits a `$ref` at each use site instead of a full copy, so
// any generated JSON Schema carrying a flow graph states each shared shape
// once rather than per node variant.
//
// Register only shapes that repeat and are larger than a `$ref`. Small enums
// and any schema carrying `.default()` cost more as a ref than inline, because
// the default forces an `allOf` wrapper around it.
z.globalRegistry.add(FlowPositionSchema, { id: 'FlowPosition' });
z.globalRegistry.add(FlowAgentNodeConfigSchema, { id: 'FlowAgentNodeConfig' });
z.globalRegistry.add(FlowFilterConfigUnionSchema, { id: 'FlowFilterConfig' });
